import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Clock,
  User,
  Bot,
  BarChart3,
  ChevronRight,
  Pause,
  Play,
  PhoneOff,
  MoreHorizontal,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { StatusBadge } from '../ui/StatusBadge';
import { CompactTranscript } from '../transcript/LiveTranscript';
import { AgentGraphMini } from '../agents/AgentGraph';
import type { Call, CallStatus, TranscriptSegment } from '../../types';

interface CallStatusPanelProps {
  calls: Call[];
  onSelectCall?: (call: Call) => void;
  selectedCallId?: string;
  className?: string;
}

interface CallCardProps {
  call: Call;
  isSelected?: boolean;
  onClick?: () => void;
  transcriptSegments?: TranscriptSegment[];
}

function useDurationCounter(startTime: string): number {
  const [duration, setDuration] = useState(() => {
    return Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setDuration(Math.floor((Date.now() - new Date(startTime).getTime()) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  return duration;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function CallCard({ call, isSelected, onClick, transcriptSegments }: CallCardProps) {
  const duration = useDurationCounter(call.startTime);
  const lastSegment = transcriptSegments?.[transcriptSegments.length - 1];

  const statusColors: Record<CallStatus, string> = {
    live: 'border-success/30 bg-success/5',
    ringing: 'border-vokentra/30 bg-vokentra/5',
    processing: 'border-warning/30 bg-warning/5',
    ended: 'border-white/10 bg-white/[0.02]',
    error: 'border-error/30 bg-error/5',
    hold: 'border-warning/30 bg-warning/5',
    queued: 'border-info/30 bg-info/5',
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      onClick={onClick}
      className={cn(
        'relative p-4 rounded-xl border cursor-pointer transition-all',
        'hover:bg-white/[0.03]',
        statusColors[call.status],
        isSelected && 'ring-2 ring-vokentra/50 bg-vokentra/5'
      )}
      whileHover={{ scale: 1.01, y: -2 }}
      whileTap={{ scale: 0.99 }}
    >
      {/* Top row: status + duration */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <StatusBadge status={call.status} size="sm" />
          {call.direction === 'inbound' ? (
            <PhoneIncoming className="w-3.5 h-3.5 text-info" />
          ) : (
            <PhoneOutgoing className="w-3.5 h-3.5 text-text-muted" />
          )}
        </div>

        <div className="flex items-center gap-1.5 text-text-muted">
          <Clock className="w-3 h-3" />
          <motion.span
            className="text-xs font-mono tabular-nums"
            key={duration}
            initial={{ opacity: 0.5 }}
            animate={{ opacity: 1 }}
          >
            {formatDuration(duration)}
          </motion.span>
        </div>
      </div>

      {/* Caller info */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
          {call.participants.find((p) => p.type === 'customer') ? (
            <User className="w-4 h-4 text-text-secondary" />
          ) : (
            <Bot className="w-4 h-4 text-vokentra" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">
            {call.participants.find((p) => p.type === 'customer')?.name || 'Unknown'}
          </p>
          <p className="text-xs text-text-muted font-mono">
            {call.participants.find((p) => p.type === 'customer')?.phoneNumber ||
              call.id.slice(0, 12)}
          </p>
        </div>
      </div>

      {/* Current agent */}
      {call.currentAgent && (
        <div className="flex items-center gap-2 mb-3 px-2 py-1.5 bg-white/[0.03] rounded-lg">
          <Bot className="w-3 h-3 text-vokentra" />
          <span className="text-xs text-text-secondary truncate">{call.currentAgent}</span>
        </div>
      )}

      {/* Mini agent graph */}
      {call.agentExecutionId && (
        <div className="mb-3">
          <AgentGraphMini
            nodeCount={4}
            activeCount={call.status === 'live' ? 2 : call.status === 'processing' ? 3 : 1}
          />
        </div>
      )}

      {/* Last transcript */}
      <CompactTranscript lastSegment={lastSegment} className="mb-3" />

      {/* Bottom: metadata */}
      <div className="flex items-center justify-between pt-3 border-t border-white/5">
        <div className="flex items-center gap-2">
          {call.metadata.reason && (
            <span className="text-[10px] px-1.5 py-0.5 bg-white/5 rounded text-text-muted truncate max-w-[100px]">
              {call.metadata.reason}
            </span>
          )}
          {call.metadata.deflected && (
            <span className="text-[10px] px-1.5 py-0.5 bg-success/10 rounded text-success">
              Deflected
            </span>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-text-muted" />
      </div>
    </motion.div>
  );
}

export function CallStatusPanel({
  calls,
  onSelectCall,
  selectedCallId,
  className,
}: CallStatusPanelProps) {
  const [statusFilter, setStatusFilter] = useState<CallStatus | 'all'>('all');
  const [transcriptMap, setTranscriptMap] = useState<Record<string, TranscriptSegment[]>>({});

  // Simulate incoming transcript segments for demo
  useEffect(() => {
    const demoSegments: TranscriptSegment[] = [
      {
        id: '1',
        callId: calls[0]?.id || '1',
        speaker: 'customer',
        text: 'Hi, my internet has been really slow for the past two days.',
        timestamp: new Date(Date.now() - 120000).toISOString(),
        confidence: 0.94,
        isPartial: false,
      },
      {
        id: '2',
        callId: calls[0]?.id || '1',
        speaker: 'ai',
        text: 'I understand your frustration. Let me run a quick diagnostic on your line to see what might be causing the slowdown.',
        timestamp: new Date(Date.now() - 115000).toISOString(),
        confidence: 0.98,
        isPartial: false,
      },
      {
        id: '3',
        callId: calls[0]?.id || '1',
        speaker: 'system',
        text: 'Network diagnostic initiated for customer C-78234',
        timestamp: new Date(Date.now() - 110000).toISOString(),
        confidence: 1,
        isPartial: false,
      },
    ];

    if (calls.length > 0) {
      setTranscriptMap({ [calls[0].id]: demoSegments });
    }
  }, [calls]);

  const filteredCalls = statusFilter === 'all' ? calls : calls.filter((c) => c.status === statusFilter);

  const statusCounts = {
    all: calls.length,
    live: calls.filter((c) => c.status === 'live').length,
    ringing: calls.filter((c) => c.status === 'ringing').length,
    processing: calls.filter((c) => c.status === 'processing').length,
    ended: calls.filter((c) => c.status === 'ended').length,
    hold: calls.filter((c) => c.status === 'hold').length,
    queued: calls.filter((c) => c.status === 'queued').length,
    error: calls.filter((c) => c.status === 'error').length,
  };

  const statusFilters: { key: CallStatus | 'all'; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'live', label: 'Live' },
    { key: 'ringing', label: 'Ringing' },
    { key: 'processing', label: 'Processing' },
    { key: 'hold', label: 'Hold' },
    { key: 'queued', label: 'Queued' },
  ];

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-vokentra" />
          <span className="text-sm font-semibold text-text-primary">Active Calls</span>
          <motion.span
            className="text-xs text-text-muted bg-white/5 px-2 py-0.5 rounded-full"
            key={calls.length}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
          >
            {calls.length}
          </motion.span>
        </div>
        <BarChart3 className="w-4 h-4 text-text-muted" />
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-1 mb-4 p-1 bg-white/[0.03] rounded-lg overflow-x-auto">
        {statusFilters.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            className={cn(
              'flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap',
              statusFilter === key
                ? 'bg-white/10 text-text-primary'
                : 'text-text-muted hover:text-text-secondary hover:bg-white/[0.02]'
            )}
          >
            {label}
            <span className="text-[10px] text-text-muted">{statusCounts[key]}</span>
          </button>
        ))}
      </div>

      {/* Call cards */}
      <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
        <AnimatePresence mode="popLayout">
          {filteredCalls.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8"
            >
              <Phone className="w-8 h-8 text-text-muted mx-auto mb-2" />
              <p className="text-sm text-text-muted">No active calls</p>
            </motion.div>
          ) : (
            filteredCalls.map((call) => (
              <CallCard
                key={call.id}
                call={call}
                isSelected={selectedCallId === call.id}
                onClick={() => onSelectCall?.(call)}
                transcriptSegments={transcriptMap[call.id]}
              />
            ))
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
